import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { fileUrl, pdfUrl, fmtEUR, fmtDateTime } from "@/lib/api";
import { STATUSES, STATUS_MAP, STATUS_DOT } from "@/lib/statuses";
import StatusPill from "@/components/StatusPill";
import {
  ArrowLeft, Download, Edit3, FileCheck2, Building2, Phone, Mail,
  CheckCircle2, Plus, X, Layers, Trash2, Copy, Archive, ArchiveRestore,
} from "lucide-react";
import { toast } from "sonner";

const QuoteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approveOpen, setApproveOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [pdfLang, setPdfLang] = useState("pt");
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [archiving, setArchiving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/quotes/${id}`);
      setQuote(data);
    } catch {
      toast.error("Cotação não encontrada");
      navigate("/cotacoes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const changeStatus = async (newStatus) => {
    if (newStatus === "encomenda_confirmada") {
      setApproveOpen(true);
      return;
    }
    setUpdating(true);
    try {
      const { data } = await api.put(`/quotes/${id}/status`, { status: newStatus });
      setQuote((q) => ({ ...q, ...data }));
      toast.success(`Status atualizado: ${STATUS_MAP[newStatus]?.label}`);
    } catch {
      toast.error("Erro ao atualizar status");
    } finally {
      setUpdating(false);
    }
  };

  const confirmApproval = async (payload) => {
    setUpdating(true);
    try {
      const { data } = await api.put(`/quotes/${id}/approve-items`, payload);
      setQuote((q) => ({ ...q, ...data }));
      setApproveOpen(false);
      toast.success("Encomenda confirmada! O PDF de Ordem de Serviço já está disponível.");
    } catch {
      toast.error("Erro ao confirmar encomenda");
    } finally {
      setUpdating(false);
    }
  };

  const deleteQuote = async () => {
    setDeleting(true);
    try {
      await api.delete(`/quotes/${id}`);
      toast.success("Cotação apagada");
      navigate("/cotacoes");
    } catch {
      toast.error("Erro ao apagar cotação");
      setDeleting(false);
    }
  };

  const duplicateQuote = async () => {
    setDuplicating(true);
    try {
      const { data } = await api.post(`/quotes/${id}/duplicate`);
      toast.success(`Cotação duplicada: ${data.number}`);
      navigate(`/cotacoes/${data.id}/editar`);
    } catch {
      toast.error("Erro ao duplicar cotação");
    } finally {
      setDuplicating(false);
    }
  };

  const toggleArchive = async () => {
    setArchiving(true);
    try {
      const action = quote.archived ? "unarchive" : "archive";
      const { data } = await api.post(`/quotes/${id}/${action}`);
      setQuote((q) => ({ ...q, ...data }));
      toast.success(quote.archived ? "Cotação desarquivada" : "Cotação arquivada");
    } catch {
      toast.error("Erro ao atualizar arquivo");
    } finally {
      setArchiving(false);
    }
  };

  if (loading || !quote) return <div className="text-zinc-500">Carregando...</div>;

  const approvedItems = quote.approved_items || [];
  const approvedMap = Object.fromEntries(approvedItems.map((a) => [a.item_id, a]));
  const isConfirmed = approvedItems.length > 0;
  const currentStatusIdx = STATUSES.findIndex((s) => s.key === quote.status);
  const range = quote.total_range || {};

  return (
    <div className="max-w-6xl mx-auto fade-up" data-testid="quote-detail-page">
      <button
        onClick={() => navigate("/cotacoes")}
        className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-300 mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> Voltar ao funil
      </button>

      <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold">Cotação</div>
            <StatusPill status={quote.status} />
            {quote.archived && (
              <span
                data-testid="archived-badge"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-700/60 text-amber-300 text-[10px] uppercase tracking-[0.15em] font-bold"
              >
                <Archive className="w-3 h-3" strokeWidth={2} /> Arquivada
              </span>
            )}
          </div>
          <h1 className="font-display text-4xl font-black text-zinc-50 tracking-tighter mb-1">
            {quote.number}
          </h1>
          <div className="flex items-center gap-2 text-zinc-400 text-sm">
            <Building2 className="w-4 h-4" strokeWidth={1.5} />
            <span>{quote.client?.name || "—"}</span>
            {quote.client?.nif && (
              <>
                <span className="text-zinc-700">·</span>
                <span className="font-mono text-xs">NIF {quote.client.nif}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div
            className="inline-flex items-center rounded-lg border border-zinc-800 bg-[#0e0e10] p-0.5"
            data-testid="pdf-lang-toggle"
            role="group"
            aria-label="Idioma do PDF"
          >
            <button
              type="button"
              onClick={() => setPdfLang("pt")}
              data-testid="pdf-lang-pt"
              className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${
                pdfLang === "pt" ? "bg-zinc-100 text-zinc-900" : "text-zinc-500 hover:text-zinc-200"
              }`}
            >
              PT
            </button>
            <button
              type="button"
              onClick={() => setPdfLang("en")}
              data-testid="pdf-lang-en"
              className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${
                pdfLang === "en" ? "bg-zinc-100 text-zinc-900" : "text-zinc-500 hover:text-zinc-200"
              }`}
            >
              EN
            </button>
          </div>
          <a
            href={pdfUrl(id, "proposal", pdfLang)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="download-proposal-pdf-btn"
            className="flex items-center gap-2 px-4 py-2.5 bg-zinc-100 hover:bg-white text-zinc-900 font-bold rounded-lg text-sm transition-all"
          >
            <Download className="w-4 h-4" strokeWidth={2} /> PDF Proposta
          </a>
          {isConfirmed && (
            <a
              href={pdfUrl(id, "order", pdfLang)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="download-order-pdf-btn"
              className="flex items-center gap-2 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold rounded-lg text-sm transition-all"
            >
              <FileCheck2 className="w-4 h-4" strokeWidth={2} /> PDF Ordem
            </a>
          )}
          <button
            onClick={() => navigate(`/cotacoes/${id}/editar`)}
            data-testid="edit-quote-btn"
            className="flex items-center gap-2 px-4 py-2.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-zinc-200 rounded-lg text-sm font-medium transition-all"
          >
            <Edit3 className="w-4 h-4" strokeWidth={1.5} /> Editar
          </button>
          <button
            onClick={duplicateQuote}
            disabled={duplicating}
            data-testid="duplicate-quote-btn"
            className="flex items-center gap-2 px-4 py-2.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-zinc-200 rounded-lg text-sm font-medium transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Copy className="w-4 h-4" strokeWidth={1.5} /> {duplicating ? "Duplicando..." : "Duplicar"}
          </button>
          <button
            onClick={toggleArchive}
            disabled={archiving}
            data-testid="archive-quote-btn"
            title={quote.archived ? "Desarquivar cotação" : "Arquivar cotação (não vai para frente)"}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all disabled:opacity-60 disabled:cursor-not-allowed ${
              quote.archived
                ? "bg-amber-500/10 hover:bg-amber-500/20 border border-amber-700/60 text-amber-300"
                : "bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-zinc-200"
            }`}
          >
            {quote.archived ? (
              <><ArchiveRestore className="w-4 h-4" strokeWidth={1.5} /> {archiving ? "..." : "Desarquivar"}</>
            ) : (
              <><Archive className="w-4 h-4" strokeWidth={1.5} /> {archiving ? "..." : "Arquivar"}</>
            )}
          </button>
          <button
            onClick={() => setConfirmDelete(true)}
            data-testid="delete-quote-btn"
            className="flex items-center gap-2 px-4 py-2.5 bg-transparent hover:bg-red-950/40 border border-red-900/60 text-red-400 hover:text-red-300 rounded-lg text-sm font-medium transition-all"
          >
            <Trash2 className="w-4 h-4" strokeWidth={1.5} /> Apagar
          </button>
        </div>
      </div>

      {/* Timeline */}
      <section className="mb-10 rounded-xl border border-zinc-800 bg-[#121214] p-6">
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-5">
          Linha do Tempo
        </div>
        <div className="relative flex items-center justify-between gap-1 mb-6">
          {STATUSES.map((s, i) => {
            const done = i < currentStatusIdx;
            const active = i === currentStatusIdx;
            return (
              <React.Fragment key={s.key}>
                <div className="flex flex-col items-center min-w-0 flex-1">
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all relative
                    ${active
                        ? "bg-zinc-100 text-zinc-900 border-zinc-100 pulse-silver"
                        : done
                          ? "bg-zinc-800 text-zinc-300 border-zinc-700"
                          : "bg-[#0e0e10] text-zinc-600 border-zinc-800"}`}
                  >
                    {done ? <CheckCircle2 className="w-4 h-4" /> : <span className="text-xs font-bold">{i + 1}</span>}
                  </div>
                  <div
                    className={`mt-2 text-[10px] uppercase tracking-[0.12em] font-bold text-center px-1 leading-tight
                    ${active ? "text-zinc-100" : done ? "text-zinc-400" : "text-zinc-600"}`}
                  >
                    {s.short}
                  </div>
                </div>
                {i < STATUSES.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mb-7 mx-1 transition-colors
                    ${i < currentStatusIdx ? "bg-zinc-600" : "bg-zinc-800"}`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        <div className="flex flex-wrap gap-2 pt-5 border-t border-zinc-800/60">
          {STATUSES.map((s) => (
            <button
              key={s.key}
              onClick={() => changeStatus(s.key)}
              disabled={updating || s.key === quote.status}
              data-testid={`status-action-${s.key}`}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-bold border transition-all
                ${s.key === quote.status
                  ? "bg-zinc-100 text-zinc-900 border-zinc-100"
                  : "bg-transparent border-zinc-800 text-zinc-400 hover:border-zinc-500 hover:text-zinc-100"}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[s.key]}`} />
              {s.label}
            </button>
          ))}
        </div>
      </section>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Items */}
        <div className="lg:col-span-2 space-y-4">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-1">
            Itens
          </div>
          {(quote.items || []).map((it, idx) => {
            const approved = approvedMap[it.item_id];
            const tiers = it.tiers || [];
            return (
              <div
                key={it.item_id || idx}
                data-testid={`detail-item-${idx}`}
                className={`rounded-xl border bg-[#121214] p-5 transition-all
                  ${isConfirmed && !approved ? "border-zinc-900 opacity-50" : "border-zinc-800"}
                  ${approved ? "border-emerald-900/60" : ""}`}
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-1">
                      Item {String(idx + 1).padStart(2, "0")}
                      {approved && <span className="ml-2 text-emerald-400">· Aprovado</span>}
                      {isConfirmed && !approved && (
                        <span className="ml-2 text-zinc-500">· Não selecionado</span>
                      )}
                    </div>
                    <h3 className="font-display text-xl font-bold text-zinc-100 tracking-tight">
                      {it.name || it.print_type}
                    </h3>
                    <div className="text-xs text-zinc-400 mt-0.5">
                      {it.print_type}
                      {it.color && (
                        <span className="ml-2 text-zinc-500">
                          · Cor: <span className="text-zinc-300">{it.color}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  {approved && (
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-400 font-bold">
                        Confirmado
                      </div>
                      <div className="font-display text-xl font-bold text-zinc-100">
                        {fmtEUR(approved.quantity * approved.price)}
                      </div>
                    </div>
                  )}
                </div>

                {/* Internal control info (never in PDF) */}
                {(it.product_code || it.supplier) && (
                  <div className="mb-4 rounded-lg border border-zinc-800/60 bg-[#0a0a0c] px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                    <span className="text-[9px] uppercase tracking-wider text-zinc-600 font-bold border border-zinc-800 rounded-full px-2 py-0.5">
                      Interno
                    </span>
                    {it.product_code && (
                      <div className="text-xs">
                        <span className="text-zinc-500">Cód. Produto:</span>{" "}
                        <span className="text-zinc-200 font-mono">{it.product_code}</span>
                      </div>
                    )}
                    {it.supplier && (
                      <div className="text-xs">
                        <span className="text-zinc-500">Fornecedor:</span>{" "}
                        <span className="text-zinc-200">{it.supplier}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Tiers table */}
                {tiers.length > 0 && (
                  <div className="rounded-lg border border-zinc-800/80 bg-[#0e0e10] overflow-hidden mb-4">
                    <div className="grid grid-cols-3 px-4 py-2 bg-[#0a0a0c] text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">
                      <div>Quantidade</div>
                      <div>Unitário</div>
                      <div className="text-right">Total</div>
                    </div>
                    {tiers.map((t, tIdx) => {
                      const q = Number(t.quantity) || 0;
                      const p = Number(t.price) || 0;
                      const isSel = approved && approved.tier_index === tIdx;
                      return (
                        <div
                          key={tIdx}
                          className={`grid grid-cols-3 px-4 py-2.5 text-sm border-t border-zinc-800/60
                            ${isSel ? "bg-emerald-950/20 text-emerald-200" : "text-zinc-200"}`}
                        >
                          <div className="font-medium flex items-center gap-2">
                            {isSel && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
                            {q} un.
                          </div>
                          <div>{fmtEUR(p)}</div>
                          <div className="text-right font-medium">{fmtEUR(q * p)}</div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {it.description && (
                  <div className="mb-4">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-1.5">
                      Descrição
                    </div>
                    <p className="text-sm text-zinc-300 whitespace-pre-line">{it.description}</p>
                  </div>
                )}

                {(it.photos || []).length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-2">
                      Fotos
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {it.photos.map((p) => (
                        <a
                          key={p}
                          href={fileUrl(p)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block aspect-square rounded-lg overflow-hidden border border-zinc-800 bg-[#0e0e10]"
                        >
                          <img src={fileUrl(p)} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Right column */}
        <div className="space-y-4">
          <div className="rounded-xl border border-zinc-800 bg-[#121214] p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1">
              {isConfirmed ? "Total Confirmado" : "Faixa de Investimento"}
            </div>
            {isConfirmed ? (
              <>
                <div className="text-xs text-zinc-500 mb-0.5">Sem IVA</div>
                <div className="font-display text-xl font-bold text-zinc-300 tracking-tight mb-3">
                  {fmtEUR(quote.confirmed_total)}
                </div>
                <div className="flex justify-between text-xs text-zinc-500 mb-2">
                  <span>IVA (23%)</span>
                  <span className="text-zinc-300">{fmtEUR(quote.confirmed_total * 0.23)}</span>
                </div>
                <div className="border-t border-zinc-800 pt-2">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-0.5">Com IVA</div>
                  <div className="font-display text-3xl font-black text-zinc-50 tracking-tighter">
                    {fmtEUR(quote.confirmed_total * 1.23)}
                  </div>
                </div>
                {(quote.extras_total || 0) > 0 && (
                  <div className="text-[10px] text-zinc-500 mt-2">
                    Subtotal inclui {fmtEUR(quote.extras_total)} em custos extras
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="text-xs text-zinc-500 mb-1">Sem IVA</div>
                <div className="font-display text-lg font-bold text-zinc-300 tracking-tight mb-2">
                  {range.min === range.max
                    ? fmtEUR(range.max || 0)
                    : `${fmtEUR(range.min || 0)} — ${fmtEUR(range.max || 0)}`}
                </div>
                <div className="border-t border-zinc-800 pt-2">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-0.5">Com IVA (23%)</div>
                  <div className="font-display text-xl font-black text-zinc-50 tracking-tighter">
                    {range.min === range.max
                      ? fmtEUR((range.max || 0) * 1.23)
                      : `${fmtEUR((range.min || 0) * 1.23)} — ${fmtEUR((range.max || 0) * 1.23)}`}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Extras (if confirmed) */}
          {isConfirmed && (quote.extras || []).length > 0 && (
            <div className="rounded-xl border border-zinc-800 bg-[#121214] p-6">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-3">
                Custos Adicionais
              </div>
              <div className="space-y-2">
                {quote.extras.map((e, i) => (
                  <div
                    key={i}
                    className="flex justify-between items-center text-sm border-b border-zinc-800/60 last:border-0 pb-2 last:pb-0"
                  >
                    <span className="text-zinc-300">{e.label}</span>
                    <span className="text-zinc-100 font-medium">{fmtEUR(e.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-xl border border-zinc-800 bg-[#121214] p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-3">
              Cliente
            </div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-zinc-900 border border-zinc-800 overflow-hidden flex items-center justify-center shrink-0">
                {quote.client?.logo_path ? (
                  <img src={fileUrl(quote.client.logo_path)} alt="" className="w-full h-full object-contain" />
                ) : (
                  <Building2 className="w-4 h-4 text-zinc-600" strokeWidth={1.5} />
                )}
              </div>
              <div className="min-w-0">
                <div className="font-medium text-zinc-100 truncate">{quote.client?.name}</div>
                {quote.client?.nif && (
                  <div className="text-xs text-zinc-500 font-mono">NIF {quote.client.nif}</div>
                )}
              </div>
            </div>
            <div className="space-y-1.5 text-xs text-zinc-400">
              {quote.contact?.name && (
                <div className="mb-2 pb-2 border-b border-zinc-800/60">
                  <div className="text-[9px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-0.5">Att / Contato</div>
                  <div className="text-zinc-100 font-medium text-sm">{quote.contact.name}</div>
                  {quote.contact.role && (
                    <div className="text-[11px] text-zinc-500">{quote.contact.role}</div>
                  )}
                  {quote.contact.email && (
                    <div className="text-[11px] text-zinc-400 mt-1">{quote.contact.email}</div>
                  )}
                  {quote.contact.phone && (
                    <div className="text-[11px] text-zinc-400">{quote.contact.phone}</div>
                  )}
                </div>
              )}
              {quote.client?.email && (
                <div className="flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5" /> {quote.client.email}
                </div>
              )}
              {quote.client?.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5" /> {quote.client.phone}
                </div>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-zinc-800 bg-[#121214] p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-3">
              Histórico
            </div>
            <div className="space-y-3">
              {(quote.timeline || [])
                .slice()
                .reverse()
                .map((t, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className={`w-1.5 h-1.5 rounded-full mt-1.5 ${STATUS_DOT[t.status]}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-zinc-200 font-medium">
                        {STATUS_MAP[t.status]?.label || t.status}
                      </div>
                      <div className="text-[10px] text-zinc-500">
                        {fmtDateTime(t.at)} · {t.by}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {quote.notes && (
            <div className="rounded-xl border border-zinc-800 bg-[#121214] p-6">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-2">
                Notas
              </div>
              <p className="text-sm text-zinc-300 whitespace-pre-line">{quote.notes}</p>
            </div>
          )}
        </div>
      </div>

      {approveOpen && (
        <ApprovalModal
          quote={quote}
          onClose={() => setApproveOpen(false)}
          onConfirm={confirmApproval}
          updating={updating}
        />
      )}

      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          data-testid="delete-confirm-modal"
        >
          <div className="w-full max-w-md rounded-2xl border border-zinc-800 bg-[#121214] shadow-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-red-950/40 border border-red-900/60 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-400" strokeWidth={1.5} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-red-400 font-bold">
                  Ação Destrutiva
                </div>
                <h3 className="font-display text-lg font-bold text-zinc-100">
                  Apagar cotação {quote.number}?
                </h3>
              </div>
            </div>
            <p className="text-sm text-zinc-400 mb-6">
              Esta ação é <span className="text-zinc-200 font-medium">irreversível</span>. Todos os dados desta cotação serão removidos permanentemente, incluindo itens, fotos, linha do tempo e informações da encomenda confirmada (se houver).
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                className="px-4 py-2.5 text-zinc-400 hover:text-zinc-100 rounded-lg text-sm transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={deleteQuote}
                disabled={deleting}
                data-testid="delete-quote-confirm-btn"
                className="flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-500 text-white font-bold rounded-lg text-sm transition-all disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" strokeWidth={2} />
                {deleting ? "Apagando..." : "Apagar definitivamente"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ApprovalModal = ({ quote, onClose, onConfirm, updating }) => {
  // selection[item_id] = { selected: bool, tier_index: number }
  const initialSel = {};
  (quote.items || []).forEach((it) => {
    const existing = (quote.approved_items || []).find((a) => a.item_id === it.item_id);
    initialSel[it.item_id] = {
      selected: existing ? true : true,
      tier_index: existing ? existing.tier_index : 0,
    };
  });
  const [sel, setSel] = useState(initialSel);

  const initialExtras = (quote.extras || []).map((e) => ({ ...e }));
  const [extras, setExtras] = useState(
    initialExtras.length > 0
      ? initialExtras
      : [
          { label: "Transporte", amount: 0 },
          { label: "Design / Arte-final", amount: 0 },
        ]
  );

  const updateSel = (itemId, patch) =>
    setSel((s) => ({ ...s, [itemId]: { ...s[itemId], ...patch } }));

  const addExtra = () => setExtras((es) => [...es, { label: "", amount: 0 }]);
  const removeExtra = (i) => setExtras((es) => es.filter((_, idx) => idx !== i));
  const updateExtra = (i, k, v) =>
    setExtras((es) => es.map((e, idx) => (idx === i ? { ...e, [k]: v } : e)));

  const itemsTotal = (quote.items || []).reduce((sum, it) => {
    const s = sel[it.item_id];
    if (!s || !s.selected) return sum;
    const t = (it.tiers || [])[s.tier_index];
    if (!t) return sum;
    return sum + (Number(t.quantity) || 0) * (Number(t.price) || 0);
  }, 0);

  const extrasTotal = extras.reduce(
    (s, e) => s + (Number(e.amount) || 0),
    0
  );
  const grandTotal = itemsTotal + extrasTotal;

  const submit = () => {
    const items = Object.entries(sel)
      .filter(([, v]) => v.selected)
      .map(([item_id, v]) => ({ item_id, tier_index: Number(v.tier_index) || 0 }));
    if (items.length === 0) {
      toast.error("Selecione ao menos um item");
      return;
    }
    const cleanExtras = extras
      .filter((e) => e.label.trim() && Number(e.amount) > 0)
      .map((e) => ({ label: e.label.trim(), amount: Number(e.amount) }));
    onConfirm({ items, extras: cleanExtras });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      data-testid="approval-modal"
    >
      <div className="w-full max-w-3xl rounded-2xl border border-zinc-800 bg-[#121214] shadow-2xl max-h-[92vh] flex flex-col">
        <div className="p-6 border-b border-zinc-800">
          <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-400 font-bold mb-1">
            Confirmar Encomenda
          </div>
          <h2 className="font-display text-2xl font-bold text-zinc-100">
            Selecione itens, quantidades e custos extras
          </h2>
          <p className="text-sm text-zinc-500 mt-1">
            Para cada item aprovado, escolha a faixa de quantidade efetivamente confirmada. Adicione transporte, design ou outros custos adicionais.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Items */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-3">
              Itens da Cotação
            </div>
            <div className="space-y-2">
              {(quote.items || []).map((it, idx) => {
                const s = sel[it.item_id] || { selected: false, tier_index: 0 };
                const tier = (it.tiers || [])[s.tier_index];
                const lineTotal = tier
                  ? (Number(tier.quantity) || 0) * (Number(tier.price) || 0)
                  : 0;
                return (
                  <div
                    key={it.item_id}
                    data-testid={`approve-item-${idx}`}
                    className={`rounded-lg border p-4 transition-all
                      ${s.selected
                        ? "border-emerald-700 bg-emerald-950/20"
                        : "border-zinc-800 bg-[#0e0e10]"}`}
                  >
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={s.selected}
                        onChange={(e) => updateSel(it.item_id, { selected: e.target.checked })}
                        className="w-4 h-4 mt-1 accent-emerald-500"
                        data-testid={`approve-item-checkbox-${idx}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-zinc-100">
                          {it.name || it.print_type}
                        </div>
                        <div className="text-xs text-zinc-500">{it.print_type}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Subtotal</div>
                        <div className="font-bold text-zinc-100">{fmtEUR(lineTotal)}</div>
                      </div>
                    </label>

                    {s.selected && (it.tiers || []).length > 0 && (
                      <div className="mt-4 ml-7">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-2 flex items-center gap-1.5">
                          <Layers className="w-3 h-3" /> Faixa Aprovada
                        </div>
                        <div className="grid sm:grid-cols-2 gap-2">
                          {it.tiers.map((t, tIdx) => {
                            const q = Number(t.quantity) || 0;
                            const p = Number(t.price) || 0;
                            const isSel = s.tier_index === tIdx;
                            return (
                              <button
                                key={tIdx}
                                type="button"
                                onClick={() => updateSel(it.item_id, { tier_index: tIdx })}
                                data-testid={`approve-tier-${idx}-${tIdx}`}
                                className={`text-left rounded-lg border p-3 transition-all
                                  ${isSel
                                    ? "border-emerald-500 bg-emerald-950/30"
                                    : "border-zinc-800 bg-[#09090b] hover:border-zinc-600"}`}
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <div className="text-sm font-bold text-zinc-100">{q} un.</div>
                                    <div className="text-xs text-zinc-500">
                                      {fmtEUR(p)} / un.
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <div className="text-sm font-bold text-zinc-100">
                                      {fmtEUR(q * p)}
                                    </div>
                                    {isSel && (
                                      <div className="text-[10px] text-emerald-400 font-bold uppercase tracking-wider">
                                        Selecionada
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Extras */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">
                Custos Adicionais
              </div>
              <button
                type="button"
                onClick={addExtra}
                data-testid="add-extra-btn"
                className="flex items-center gap-1.5 text-xs text-zinc-300 hover:text-zinc-100 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> Adicionar custo
              </button>
            </div>
            <div className="space-y-2">
              {extras.map((e, i) => (
                <div
                  key={i}
                  className="grid grid-cols-[1fr,150px,32px] gap-2 items-center"
                  data-testid={`extra-row-${i}`}
                >
                  <input
                    value={e.label}
                    onChange={(ev) => updateExtra(i, "label", ev.target.value)}
                    placeholder="Ex: Transporte, Design, Manuseio, Empacotamento..."
                    className="px-3 py-2 bg-[#09090b] border border-zinc-800 rounded-lg text-zinc-100 placeholder-zinc-600 focus:border-zinc-500 focus:outline-none text-sm"
                    data-testid={`extra-label-${i}`}
                  />
                  <div className="relative">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={e.amount}
                      onChange={(ev) => updateExtra(i, "amount", ev.target.value)}
                      placeholder="0,00"
                      className="w-full pl-3 pr-8 py-2 bg-[#09090b] border border-zinc-800 rounded-lg text-zinc-100 placeholder-zinc-600 focus:border-zinc-500 focus:outline-none text-sm"
                      data-testid={`extra-amount-${i}`}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-500">€</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeExtra(i)}
                    className="w-8 h-8 flex items-center justify-center text-zinc-500 hover:text-red-400 rounded transition-colors"
                    aria-label="Remover custo"
                    data-testid={`remove-extra-${i}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
            {extras.length === 0 && (
              <div className="text-xs text-zinc-600 italic">
                Nenhum custo extra adicionado.
              </div>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-zinc-800 flex items-end justify-between gap-4 flex-wrap">
          <div className="space-y-0.5">
            <div className="text-xs text-zinc-500 flex justify-between gap-4">
              <span>Subtotal itens:</span>
              <span className="text-zinc-300 font-medium">{fmtEUR(itemsTotal)}</span>
            </div>
            <div className="text-xs text-zinc-500 flex justify-between gap-4">
              <span>Custos extras:</span>
              <span className="text-zinc-300 font-medium">{fmtEUR(extrasTotal)}</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mt-2">
              Total da Encomenda
            </div>
            <div className="font-display text-2xl font-black text-zinc-100">{fmtEUR(grandTotal)}</div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2.5 text-zinc-400 hover:text-zinc-100 rounded-lg text-sm"
            >
              Cancelar
            </button>
            <button
              onClick={submit}
              disabled={updating}
              data-testid="approval-confirm-btn"
              className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold rounded-lg text-sm disabled:opacity-50"
            >
              <CheckCircle2 className="w-4 h-4" /> Confirmar Encomenda
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteDetail;
